/*
* This component handles the languages available based on the country and a login/signup button.
*/
import React, { useContext, useEffect, useState,useRef } from "react";
import {
	Grid,
	FormControl,
	Select,
	MenuItem,
	Button,
	Box,
	Menu,
	ListSubheader,
	Typography,
	Popper
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import "./style.scss";
import { useTranslation } from "react-i18next";
import i18n from "../../../utils/i18n";
import LocaleContext from "../../../context/LocaleContext";
import {
	getFxcmLogo,
	MenuIcon,
	CloseIcon,
	liveLogo
} from "../../../constants/ImageConstants";
import { useDispatch, useSelector } from "react-redux";
import { configurationServices } from "../../../constants/configurationServices";
import { logoutService } from "../../../services/auth.service";
import {
	getCurrentUrlPath,
} from "../../../utils/helpers";
import FXCMPopUp from "../../../components/common/FXCMPopUp";
import FXCMDrawer from "../../common/FXCMDrawer";
import Drawer from "../../Drawer";
import { languagequeryParam, setUserEntity, arabicLanguagequeryParam, additionalProperties, setAllEnLabels } from "../../../reducers/signUpSlice";
import { notifyApiFail } from "../../../reducers/errorNotifySlice";
import { getUserDetails } from "../../../utils/getUsersDetails";
import { analytics } from "../../../utils/analytics/analytics";
import useDirectionHook from "../../../hooks/useDirectionHook";
import { storelanguageList, setContactLink } from "../../../reducers/commonSlice";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { updateUserInfo } from "../../../reducers/userInfoSlice";
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { WelcomeBackContext } from "../../../context/WelcomeBackProvider";
import { resolveLocaleEntity } from "../../../utils/entityLocaleFolder";

const countriesWithLangWarning = ["DE", "CN"];

// Entity-neutral pages: every language is offered, and each one is loaded from
// an entity folder that actually ships it (CX-51034).
const AUTH_PATHS = ["/login", "/forgotpassword", "/forgotusername"];
const isAuthPage = () => AUTH_PATHS.includes(getCurrentUrlPath());

const Header = (props) => { // NOSONAR
	const dispatch = useDispatch();
	const { country } = props;
	const { t: getLabel } = useTranslation();
	const { locale } = useContext( LocaleContext );
	const { hideWelcomeBack } = useContext( WelcomeBackContext ) ?? {};
	const {
		languageParam,
		authRes,
		userEntity,
		loginRes,
		authToken,
		rbName,
		getUserLocation,
		arabicLanguageParam,
		autoLoginResponse, 
		langList
	} = useSelector((state) => state.signUp);
	const { ip } = useSelector((state) => state.ipAddress);
	const { user } = useSelector((state) => state.user);
	const { contactLink } = useSelector((state) => state.common);
	const [languageList, setLanguageList] = useState();
	const navigate = useNavigate();
	const selLang = DOMPurify.sanitize(localStorage.getItem("i18nextLng")) || 'en-US';
	const [profileName, setProfileName] = useState("");
	const [headerEmail, setHeaderEmail] = useState("");
	const [showSignUp, setShowSignUp] = useState();
	const [showForgotPassword, setShowForgotPassword] = useState();
	const [showLogin, setShowLogin] = useState();
	const [liveChat, setLiveChat] = useState({ width: window.innerWidth });
	const [showForgotUsername, setShowForgotUsername] = useState();
	const [data, setData] = useState("");
	const [contactUsConfig, setContactUsConfig] = useState("");
	const [languageNotSupport, setLanguageNotSupport] = useState(false);
	const [direction] = useDirectionHook(languageParam) //custom hook
	const [userDetails, setUserDetails] = useState({
		accountNumber: "",
		mt4Server: "",
		isMT4: false,
	});

	const headerRef =useRef(null);
	const [headerHeight,setHeaderHeight] =useState(0);
    const [maxHeight, setMaxHeight] = useState(448);
	const location = useLocation();
    const prevLangRef = useRef(languageParam);
	const latestTargetLangRef = useRef(null);
	let locationName = location.pathname;
  
	const esmaCondition =
		userEntity === "FXCM_AU" ||
		userEntity === "FXCM_UK" ||
		userEntity === "FXCM_SVG" ||
		(
			userEntity === "FXCM_EU" &&
			(
				locationName === "/rejection" ||
				locationName === "/success" ||
				locationName === "/docUpload" ||
				locationName === "/deposit" ||
				locationName === "/deposit/confirmation" ||
				locationName === "/tax"
			)
		)

	const isDashboardScreen =
	locationName === "/docUpload" ||
	locationName === "/deposit" ||
	locationName === "/deposit/confirmation" ||
	locationName === "/tax"
  
	const localeMenu = userEntity === "FXCM_EU" || userEntity === "FXCM_UK";

	const menuIcon_Button =
		props.screenType !== "login" &&
		props.screenType !== "forgotusername" &&
		props.screenType !== "forgotpassword";

	useEffect(() => {
		const handleResize = () => {
			setAnchorEl(null);
		};
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		}
	}, []);

	useEffect(() => {
		if (
			getCurrentUrlPath() === "/" && langList
		) {
			fetchLanguageJson();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEntity, getUserLocation, langList, languageList]);
	
	// dispatcher for notifying language change due to country change
	useEffect(() => {
		let staleLang = DOMPurify.sanitize(localStorage.getItem("PRE_SELECTED_LANG") || "");
		if (
			countriesWithLangWarning.includes(country) &&
			locale !== staleLang &&
			!languageList?.find((item) => item.key === staleLang)
		) {
			dispatch(
				notifyApiFail({
					title: getLabel("UNSUPPORTED_LANGUAGAGE", { lng: staleLang }),
					desc: getLabel("ENGLISH_NOT_SUPPORT", { lng: staleLang }),
					type: "warning",
				}),
			);
			localStorage.setItem("PRE_SELECTED_LANG", locale);
    	} else if(country !== "" && country !== undefined && (!countriesWithLangWarning.includes(country) || locale !== staleLang)) {
			localStorage.setItem("PRE_SELECTED_LANG", locale);
		}
	}, [country, languageList, locale])

	/* istanbul ignore next */
	useEffect(() => {
	/* This is for showing only German language in the Language list when country is Germany */
    const currentLang = languageParam;
    const previousLang = prevLangRef.current;

    if (country === "DE") {
        if (currentLang !== "de-DE" || (previousLang && previousLang !== "de-DE")) {
            if (!languageNotSupport) {
                setLanguageNotSupport(true);
            }
        }
    } else {
        // Reset when moving away from Germany
        setLanguageNotSupport(false);
    }

    // Always update the ref at the end of the effect
    prevLangRef.current = languageParam;
}, [country, languageParam, dispatch, getLabel]);


	
		/* istanbul ignore next */
	const fetchNoticeJson = () => {
		fetch(configurationServices(userEntity).popUpConfigURL)
			.then((res) => {
				return res.json();
			})
			.then((data) => {
				setData(data);
			})
			.catch((e) => {
				console.log(e.message);
			});
	};
	useEffect(() => {
			fetchNoticeJson();

			fetch(configurationServices(userEntity).contactnumberCardURL)
				.then((res) => {
					return res.json();
				})
				.then((data) => {
					setContactUsConfig(data);
				})
				.catch((e) => {
					console.log(e.message);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userEntity]);

	useEffect(() => {
		setShowSignUp(!authRes && getCurrentUrlPath() !== "/");
		setShowLogin(!authRes && getCurrentUrlPath() !== "/login");
		setShowForgotPassword(
			!authRes && getCurrentUrlPath() !== "/forgotpassword"
		);
		setShowForgotUsername(
			!authRes && getCurrentUrlPath() !== "/forgotusername"
		);
		setHeaderEmail(
			user?.email
				? user.email
				: loginRes?.data?.["user-details"] // NOSONAR
				? loginRes?.data?.["user-details"].email
				: ""
		);
		settingProfileName(
			user?.aboutYourself
				? user?.aboutYourself
				: loginRes?.data?.["user-details"]
		);
	}, [authRes, loginRes, user]);
	/* istanbul ignore next */
	useEffect(() => {
		const requiredLocations = ["/docUpload", "/userInfo", "/deposit", "/tax"];
		if (user?.authToken && requiredLocations?.includes(locationName)) fetchUserDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.authToken]);
		/* istanbul ignore next */
	const fetchUserDetails = async () => {
		const response = await getUserDetails(user?.authToken, "header");
		const responseData = response?.data?.["user-details"];
		setUserDetails({
			...userDetails,
			mt4Server: responseData?.databaseOfficeId?.split(/\s*,\s*/)[0], // NOSONAR
			accountNumber: responseData?.fxowsAccountNumber,
			isMT4: (responseData?.platform === 'MT')
		} );
		dispatch(setUserEntity(responseData?.entity));
		let useinfoData = {
			countryOfResidence: responseData?.countryOfResidenceCode,
			locale: responseData?.userLocale,
			userName : responseData?.username,
		}
		dispatch( updateUserInfo( useinfoData ) );
		dispatch(languagequeryParam(responseData?.userLocale));
	};
	/* istanbul ignore next */
	useEffect(() => {
		if ( autoLoginResponse?.success && loginRes?.data ){
	
			let autoLoginMessage = autoLoginResponse?.data?.["login-response"]?.message;
			if (autoLoginMessage === "Login Success") {
				dispatch(setUserEntity(loginRes?.data["user-details"]?.entity));
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	},[autoLoginResponse])

	
	const fetchLanguageJson = () => {
        const filteredData = Object.fromEntries(Object.entries(langList).filter(([_, value]) =>
            value.entity.includes(userEntity)
        ));
		const processed = processLanguageData(filteredData);
		setLanguageList(prev => {
		  const isSame = JSON.stringify(prev) === JSON.stringify(processed);
		  return isSame ? prev : processed;
		});
		handleLanguageWarnings(processed);
		dispatch(storelanguageList(processed));

      };
	  /* istanbul ignore next */
	  const processLanguageData = (data) => {
		const newLanguageList = Object.entries(data).map(([key, value]) => ({ key, value }));
		const finalLanList = [];
		const defaultLanguageCountry = [];
	  
		const preSelectedCountry = DOMPurify.sanitize(sessionStorage.getItem("PRE_SELECTED_COUNTRY") || "");

		newLanguageList.forEach((element) => {
			if (preSelectedCountry === "DE" && element.key !== "de-DE") {
				return; 
			}
		  if (isEnabledForUserLocation(element)) {
			finalLanList.push(element);
		  }
	  
		  if (element.value.defaultLanCountry === getUserLocation) {
			defaultLanguageCountry.push(element);
			if (!authToken) {
                dispatch(languagequeryParam(defaultLanguageCountry[0]?.key)); //initial load
            }
            // authToken branch intentionally does nothing now: languageParam is already kept in
            // sync with i18next's active language elsewhere (the [languageParam, userEntity]
            // effect below, and App.js's languageChanged listener). Re-reading i18nextLng from
            // localStorage here raced with that async reloadResources/changeLanguage chain —
            // this function re-runs on every fetchLanguageJson call, so the stale read kept
            // re-dispatching an outdated language and fought with the corrected value, producing
            // a zh-CN/en-US (or any defaultLanCountry language) flicker on entity switches.
		  }
		});
	  
		return finalLanList;
	  };
	  
	  /* istanbul ignore next */
	  const isEnabledForUserLocation = (element) => {
		return !element?.value?.disableCountry || !element.value.disableCountry.includes(getUserLocation);
	  };
	  
	  /* istanbul ignore next */
	  const handleLanguageWarnings = (languageList) => {
		if (isUnsupportedLanguageSelected(languageList)) {
		  dispatch(notifyApiFail({
			title: getLabel('LOCALE_UNSUPPORTED_TITLE'),
			desc: getLabel('LOCALE_UNSUPPORTED_SUBTITLE'),
			type: 'warning'
		  }));
		}
	  
		if (arabicLanguageParam && languageParam === "zh-CN") {
		  dispatch(notifyApiFail({
			title: getLabel('LOCALE_UNSUPPORTED_TITLE'),
			desc: getLabel('LOCALE_UNSUPPORTED_SUBTITLE'),
			type: 'arabicWarning'
		  }));
		}
	  
		if (languageParam === "ar-AE") {
		  dispatch(arabicLanguagequeryParam(true));
		}
	  };
	  
	  /* istanbul ignore next */
	  const isUnsupportedLanguageSelected = (languageList) => {
		return languageList.find((element) => element.key === languageParam) &&
		  !languageList.find((item) => item.key === languageParam);
	  };
	  
	useEffect(() => {
		if (langList && isAuthPage()) {
			setLanguageList(Object.entries(langList).map(([key, value]) => ({ key, value })));
		}
	}, [langList]);

	useEffect(() => {
		if (languageParam && userEntity) {
			const localeEntity = isAuthPage() ? resolveLocaleEntity(languageParam, userEntity, langList) : userEntity;
			localStorage.setItem("SELECTED_ENTITY", localeEntity);
			const isLangValid = langList
				? Boolean(langList[languageParam]?.entity?.includes(localeEntity))
				: true;
			const targetLang = isLangValid ? languageParam : "en-US";
			latestTargetLangRef.current = targetLang;
			i18n.reloadResources(targetLang).then(() => {
				// Stale-response guard: if this effect fired again for a different targetLang
				// before this reloadResources call resolved, latestTargetLangRef will have moved
				// on. Without this check, two overlapping reloadResources/changeLanguage chains
				// (e.g. one for "en-US", one for "zh-CN") resolve independently in whatever order
				// the network returns them, each firing its own languageChanged event and each
				// event re-dispatching languageParam — which re-triggers this very effect and
				// starts another overlapping chain, forever. Only the most recently requested
				// language's resolution is allowed to actually apply.
				if (latestTargetLangRef.current !== targetLang) {
					return;
				}
				i18n.changeLanguage(targetLang);
				if (targetLang !== languageParam) {
					dispatch(languagequeryParam(targetLang));
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [languageParam, userEntity]);
		/* istanbul ignore next */
	const settingProfileName = (aboutYourself) => {
		let profileName = "";
		if (aboutYourself) {
			const firstName = aboutYourself?.firstName?.replace(
				/^.{1}/g, // NOSONAR
				aboutYourself?.firstName[0]?.toUpperCase()
			);
			const lastName = aboutYourself?.lastName?.replace(
				/^.{1}/g, // NOSONAR
				aboutYourself?.lastName[0]?.toUpperCase()
			);
			if (firstName && lastName) profileName = `${firstName} ${lastName}`;
		}
		setProfileName(profileName);
	};

	useDocumentTitle(getLabel("APP_TITLE"));
	/* istanbul ignore next */
	const onLanguageChange = ( event ) =>{
		const l = event.target.value;
		sessionStorage.setItem( "PRE_SELECTED_COUNTRY", country );
		localStorage.setItem("PRE_SELECTED_LANG", l);
		if (locale !== l) {
			if (isAuthPage()) {
				// i18n's loadPath reads SELECTED_ENTITY — point it at a folder that ships `l`.
				localStorage.setItem("SELECTED_ENTITY", resolveLocaleEntity(l, userEntity, langList));
			}
			latestTargetLangRef.current = l;
			i18n.reloadResources(l).then(() => {
				if (latestTargetLangRef.current !== l) {
					return;
				}
				return i18n.changeLanguage(l).then(() => {
					// getAllEnLabels must always hold the English bundle, regardless of which
					// language the user just switched to (l) — it feeds the "english" field of
					// backend payloads alongside the translated "locale" field. loadLanguages
					// force-fetches the current entity's en-US bundle if it isn't cached yet.
					i18n.loadLanguages("en-US", () => {
						const labels = i18n.getResourceBundle("en-US", "language");
						dispatch(setAllEnLabels(labels));
					});
				});
			});
			getFxcmLogo(selLang);
			dispatch(arabicLanguagequeryParam(false))
			dispatch(languagequeryParam(l)); // new
			localStorage.setItem("i18nextLng", l);
		}
		if(getCurrentUrlPath() === "/login") {
            if(direction.status) {
				navigate("/login")
			}
		}
	};

	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean( anchorEl );
		/* istanbul ignore next */
	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
  /* istanbul ignore next */

    const handleLogOut = () => {
		if (ip) {
			analytics?.trackEvent( "Logged_out", {});
		    dispatch(
				logoutService({ 
					logout: true, ipAddress: ip, 
					logoutRedirectURL: `/login?lang=${languageParam}`, 
					navigate: navigate 
					},
				    hideWelcomeBack
					)
				);
		    }
	};
	/* istanbul ignore next */
	const goToSignupPage = () => {
		dispatch(additionalProperties(loginRes));
		navigate("/");
	};
	const goToLoginPage = () => {
		navigate("/login");
	};

	/* istanbul ignore next */
	  useEffect(()=>{
           if(headerRef.current){
			setHeaderHeight(headerRef?.current?.clientHeight)
		   }
	  },[])

	/* istanbul ignore next */
	  useEffect(()=>{
        const handleResize = () =>{
			const viewportHeight = window?.innerHeight;
			const availableSpace = viewportHeight - headerHeight -50 //adjusting margin
			setMaxHeight(Math.min(availableSpace,468)) // Ensure it doesn't exceed 468px
		  }
			handleResize();
			window.addEventListener("resize",handleResize);
			return ()=>window.removeEventListener("resize",handleResize);
		
	  },[headerHeight]);

		const DEFAULT_LOCALE = "en-US";

		// 1. Try to find matching locale with COR logic
		let contactUsObj = contactUsConfig?.CONTACT_US_URL?.find(item =>
		item?.locale === languageParam &&
		(
			!Array.isArray(item?.COR) ||
			item?.COR?.includes(user?.countryOfResidence || country)
		)
		);

		// 2. Fallback: en-US WITHOUT COR
		if (!contactUsObj) {
		contactUsObj = contactUsConfig?.CONTACT_US_URL?.find(item =>
			item?.locale === DEFAULT_LOCALE &&
			!Array.isArray(item?.COR)
		);
		}

		const contactUslink = contactUsObj?.link;
		
		useEffect(() => {
			dispatch(setContactLink(contactUslink));
		}, [contactUslink,dispatch]);

		const initializeContactUs = () => {
			if (contactUslink && contactUslink.startsWith("https://")) {
				window.open(
					contactUslink,
					"_blank",
					`width=${Math.floor(window.innerWidth * 0.75)},height=${window.innerHeight},noopener`
				);
			}
		};

	const { width } = liveChat;
	const isMobile = width <= 599;
	return (
			<div className="custom-header" id="fxcm-header" ref={headerRef}>
				<div className="container">
					<Grid
						container
						spacing={0}
						justifyContent="space-between"
						alignItems="center"
					>
						<Grid item>
							<div className="logo-FXCM" id="fxcm-header-logo">
								<img src={getFxcmLogo(selLang)} alt="logo" />
							</div>
						</Grid>

						<Grid item className="referring-grid">
							<Box display="flex">
								{(rbName && props.screenType !== "login" && !isMobile ? ( // NOSONAR
									<FormControl className="custom-country-control refering_broker">
										{getLabel("REFERRING_BROKER")} : {rbName}
									</FormControl>
								) : (""))}
								{(!authRes && locationName !== "/resetPassword") && (
									<FormControl className="custom-country-control">
										<Select
											id="fxcm-header-lang-select"
											value={languageNotSupport ? "de-DE" : locale}
											onChange={onLanguageChange}
											className="custom-country-dropdown"
											MenuProps={{
												className: localeMenu
												       ? "custom-dropdown-list-esmaBanner"
												       : "custom-dropdown-list",
												PopperComponent:(props)=>( // NOSONAR
													<Popper
													 {...props}
													 anchorEl={anchorEl}
													 placement="bottom-start"
													 modifiers={[
														{
															name:"preventOverflow",
															options:{
																boundary:"viewport"
															}
														}
													 ]}
													/>
												),
												PaperProps:{
													style:{
														maxHeight:`${maxHeight}px`,
														overflow:"auto",
														width:"338px"
													}
												}
											}}
											displayEmpty
											inputProps={{ "aria-label": "Without label" }}
											sx={{
												".MuiSvgIcon-root": {
													background: `url(${
														process.env.REACT_APP_CDN_URL + "/images/Close.svg"
													})`
												},
												".MuiSelect-select": {
													"&::before": {
														background: `url(${
															process.env.REACT_APP_CDN_URL +
															"/images/world-icon.svg"
														})`
													}
												}
											}}
											data-testid="select-language"
										>
										<div style={{position:"static", width: "100%"}}>
											<ListSubheader className="help-text">
													{getLabel("LANGUAGE_HELPTEXT")}
											</ListSubheader>
										</div>	
										
											{/* istanbul ignore next */
												languageNotSupport
												? languageList
														?.filter((item) => item.key.includes("de-DE"))
														.map((item, key) => {
															return (
																<MenuItem id= {`fxcm-header-lang-select-${item?.key}`} key={item.key} value={item.key}>
																	{item.value.nativeName}
																</MenuItem>
															);
														})
												: languageList?.map((item, key) => {
														return (
															<MenuItem id= {`fxcm-header-lang-select-${item?.key}`} key={item.key} value={item.key}>
																{item.value.nativeName}
															</MenuItem>
														);
												  })}
										</Select>
									</FormControl>
								)}
								{showSignUp && showForgotUsername && showForgotPassword && (
									<Button
										id= "fxcm-header-signup-btn"
										variant="text"
										className="button-text"
										onClick={() => goToSignupPage()}
									>
										{getLabel("SIGN_UP")}
									</Button>
								)}
								{showLogin && (
									<Button
										id= "fxcm-header-login-btn"
										variant="text"
										className={
											rbName && menuIcon_Button
												? "button-text_rb"
												: "button-text"
										}
										onClick={() => goToLoginPage()}
									>
										{getLabel("LOGIN_HEADING1")}
									</Button>
								)}

								{isMobile ? (
									<div className="Profile_Mobile">
										{authRes && 
										<Button
											variant="text"
											className={isMobile ? "button-Mobile button-liveChat" : `button-text button-liveChat ${direction ? "button-textArabic" : ""}`} // NOSONAR
											onClick={initializeContactUs}
											id="liveagent_button_online_ID"
											sx={{ mt: !isMobile && 1.5 }}
										>
											<img
												src={liveLogo}
												alt="livelogo"
												width="25"
												height="24"
												className={isMobile ? "button-text-Mobile" : "liveLogo"}
											/>
										</Button>
										}
										{((rbName && menuIcon_Button) || authRes) && (
											<Button
												id="basic-button"
												aria-controls={open ? "basic-menu1" : undefined}
												aria-haspopup="true"
												aria-expanded={open ? "true" : undefined}
												onClick={handleClick}
												className="menu-class"
											>
												{open ? (
													<img
														src={CloseIcon}
														alt="menuicon"
														width="24"
														height="24"
														className="menu-icon"
													/>
												) : (
													<img
														src={MenuIcon}
														alt="menuicon"
														width="24"
														height="24"
														className="menu-icon"
													/>
												)}
											</Button>
										)}
										<Menu
											id="basic-menu"
											anchorEl={anchorEl}
											open={open}
											onClose={handleClose}
											className={
												esmaCondition
                                                     ? direction?.status ? "custom-dropdown-menu-Arabic": "custom-dropdown-menu" // NOSONAR
                                                     : direction?.status ? "custom-dropdown-menu-Arabic-esmabanner" :"custom-dropdown-menu-esmabanner" // NOSONAR
											}
											MenuListProps={{
												"aria-labelledby": "basic-button"
											}}
										>
											<Typography className="name-style">
												{authRes && profileName ? profileName : ""}
											</Typography>
											{authRes && (
												<Typography className="username-style">
													{getLabel("EMAIL")}:{" "}
													{authRes && headerEmail ? headerEmail : ""}
												</Typography>
											)}
											{isDashboardScreen && userDetails?.accountNumber && (
												<Typography className="username-style">
													{getLabel("ACCOUNT_NUMBER")}:{" "}
													{isDashboardScreen && userDetails?.accountNumber}
												</Typography>
											)}
											{isDashboardScreen && userDetails?.mt4Server && userDetails?.isMT4 && (
													<Typography className="username-style">
														{getLabel("MT4_SERVER")}: {isDashboardScreen && userDetails?.mt4Server}
													</Typography>
											 )}
											{rbName ? (
												<div className="refering_broker">
													{getLabel("REFERRING_BROKER")} : {rbName}
												</div>
											) : (
												""
											)}
											{authRes && (
												<FXCMDrawer
													buttontext={getLabel(data.saveANDlogout)}
													className={direction?"save-and-logoutArabic":"save-and-logout"}
													logoutIcon={true}
													content={
														<Drawer
															popUpTitle={getLabel(data.popupTitle)}
															popupSubtitle={getLabel(data.popupSubtitle)}
															handleLogout={handleLogOut}
															logout={getLabel(data.logout)}
															continue={getLabel(data.continue)}
														/>
													}
												/>
											)}
										</Menu>
									</div>
								) : (
									authRes && (
										<div className="Profile_Desktop">
											<FormControl className="custom-dropdown-controlar">
												<Button
													id="basic-button"
													aria-controls={open ? "basic-menu" : undefined}
													aria-haspopup="true"
													aria-expanded={open ? "true" : undefined}
													onClick={handleClick}
												>
													{authRes && headerEmail ? headerEmail : ""}
												</Button>
												<Menu
													id="basic-menu"
													anchorEl={anchorEl}
													open={open}
													onClose={handleClose}
													className="custom-dropdown-menu Profile_Desktop"
													MenuListProps={{
														"aria-labelledby": "basic-button"
													}}
												>
													<Typography className="name-style">
														{authRes && profileName ? profileName : ""}
													</Typography>
													<Typography className="username-style">
														{getLabel("EMAIL")}:{" "}
														{authRes && headerEmail ? headerEmail : ""}
													</Typography>
													{isDashboardScreen && userDetails?.accountNumber && (
														<Typography className="username-style">
															{getLabel("ACCOUNT_NUMBER")}:{" "}
															{isDashboardScreen && userDetails?.accountNumber}
														</Typography>
													)}
													{isDashboardScreen && userDetails?.mt4Server && userDetails?.isMT4 && (
														<Typography className="username-style">
															{getLabel("MT4_SERVER")}:{" "}
															{isDashboardScreen && userDetails?.mt4Server}
														</Typography>
													 )}
													<FXCMPopUp
													className={direction?"save-and-logoutArabic":"save-and-logout"}
														popUpTitle={getLabel(data.popupTitle)}
														popupSubtitle={getLabel(data.popupSubtitle)}
														buttontext={getLabel(data.saveANDlogout)}
														handleLogout={handleLogOut}
														logout={getLabel(data.logout)}
														continue={getLabel(data.continue)}
														logoutIcon={"logoutIcon"}
													/>
												</Menu>
											</FormControl>
											<Button
												variant="text"
												className={isMobile ? "button-Mobile button-liveChat" : `button-text button-liveChat ${direction ? "button-textArabic" : ""}`} // NOSONAR
												onClick={initializeContactUs}
												id="liveagent_button_online_ID"
												sx={{ mt: !isMobile && 1.5 }}
											>
												{getLabel("CONTACT_US")}
												<img
													src={liveLogo}
													alt="livelogo"
													width="25"
													height="24"
													className={isMobile ? "button-text-Mobile" : "liveLogo"}
												/>
											</Button>
										</div>
									)
								)}
							</Box>
						</Grid>
					</Grid>
				</div>
			</div>
	);
};

Header.propTypes = {
	country: PropTypes.string,
	screenType: PropTypes.string
}

export default Header;